import {
	Box,
	Grid,
	InputAdornment,
	TextField,
	styled,
	ToggleButton,
	ToggleButtonGroup,
	Stack,
	debounce,
	Typography,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PageTitle from 'components/PageTitle/PageTitle';

import { getRegulationInventoryData, getExportColNames } from 'common/api';
import { ITabValues } from 'common/api/type-def';
import { ReactComponent as Plus } from 'common/assets/images/Plus.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import { ReactComponent as VectorExport } from 'common/assets/images/vector-export.svg';
import Banner from 'common/components/Banner/Banner';
import ButtonComp from 'common/components/Button/ButtonComp';
import ExportData from 'common/components/ExportData/ExportData';
import { ISelectOption } from 'common/components/MulltipleSelectCheckmarks/MultipleSelectCheckmarks';
import TabBar from 'common/components/TabBar/TabBar';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	MODULES,
	PAGINATION_CONSTANTS,
	TABLE_VIEW_PAGE_SIZE,
} from 'common/constants';
import {
	FILTER_TYPE,
	PROGRESS_BAR_ELEMENTS,
} from 'common/constants/CitationDetails';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	REGULATION_TAB_VALUES,
	REG_INV_REGULATION_SEARCH_TYPE,
} from 'common/constants/RegulationInventoryConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import {
	APP_GENERAL_ERROR_MSG,
	RegulatoryInventoryLanding,
	REG_INV_NO_RECORDS_ERROR_MSG,
} from 'common/constants/TextContent';
import storageWrapper from 'common/loginUtils/storage-wrapper';
import { RootState } from 'common/store/AppStore';
import {
	setRegInvententoryCreateDrawer,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import {
	setRegInventorySearchText,
	setRegulationToggleButton,
} from 'common/store/reducer/regulationInventoryReducer';
import {
	formatNumber,
	scrollToTop,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';
import useTableQuery from 'hooks/useTableQuery';

import { handleSpecialCharacters } from 'features/RiskProfile/Taxonomy/TaxonomyUtils';

import {
	IRegulationInventoryList,
	IGroupsTableList,
	IRegInvSearchParams,
	IExportDataResponse,
} from '../api/type-def';
import AdvancedFilter from '../Components/AdvancedFilter/AdvancedFilter';
import RegulationBreadcrumbs, {
	ICustomBreadcrumbsProps,
} from '../Components/RegulationBreadcrumb/RegulationBreadcrumbs';
import {
	tableUniqueValues,
	getKeysForRegInvSearch,
	getRegInvRegulationSearchIndexData,
	getMappedColumnNamesFromResponse,
	TAB_VALUES,
	NO_COLUMN_ERROR_MESSAGE,
} from '../RegInventoryUtils';

import AllRegulationsTable from './AllRegulations/AllRegulationsTable';
import CompleteTabTable from './CompleteTab/CompleteTab';
import GroupsTable from './GroupingTable/GroupsTable';
import MappingTable from './MappingTable/MappingTable';

import styles from './RegulationInventoryLandingpage.module.scss';

const {
	searchFieldBackground,
	searchFieldBorderRadius,
	selectedTextColor,
	selectedBackground,
	activeIconColorPrimary,
	exportDisabled,
} = styles;

const { REGULATION, GROUP } = REG_INV_REGULATION_SEARCH_TYPE;

const {
	NOTSTARTED,
	GROUPING_ASSESSMENT,
	APPLICABILITY_ASSESSMENT,
	GROUPING_REVIEW,
	APPLICABILITY_REVIEW,
	SUMMARY_ASSESSMENT,
	SUMMARY_REVIEW,
	MAPPING_ASSESSMENT,
	MAPPING_REVIEW,
	COMPLETE,
} = PROGRESS_BAR_ELEMENTS;

const { REQUIREMENTS, OBLIGATIONS, EXPORT } = TAB_VALUES;

const { REQUIREMENT_INVENTORY } = MODULES;

const { WORK_FLOW_STATUS_TYPE } = FILTER_TYPE;

const { OK } = HTTP_STATUS_CODES;

const { NO_COLUMN_DATA } = NO_COLUMN_ERROR_MESSAGE;

const { CURRENT_INVENTORY } = REGULATION_TAB_VALUES;

export const StyledTextField = styled(TextField)({
	background: searchFieldBackground,
	borderRadius: searchFieldBorderRadius,
	width: '100%',
});

const toggleButtonStyle = {
	select: {
		textTransform: 'none',
		border: 'none',
		'&.Mui-selected': {
			margin: '8px 8px 0px 8px',
			color: selectedTextColor,
			height: '32px',
			alignItems: 'center',
			gap: '8px',
			backgroundColor: selectedBackground,
			borderRadius: '2px',
			minHeight: '1.5rem',
			'@media (max-width: 1024px)': {
				width: 'min-content',
				height: 'fit-content',
				gap: '5px',
			},
			'&:hover': {
				backgroundColor: selectedBackground,
				color: selectedTextColor,
			},
		},
		'&.Mui-focusVisible': {
			backgroundColor: selectedBackground,
		},
		'&:hover': {
			backgroundColor: selectedTextColor,
		},
	},
};

interface IRegInvLandingPage {
	wrapper?: any;
}

interface IRegInvLandingPageData {
	totalRecords: IRegInvLandingData;
	applicabilityRecords: IRegInvLandingData;
	groupingRegulationRecords: IRegInvLandingData;
	groupingRecords: IRegInvLandingData;
	summaryRecords: IRegInvLandingData;
	mappingRecords: IRegInvLandingData;
	completedRecords: IRegInvLandingData;
}

interface IRegInvLandingData {
	id: string;
	errorMessage: string;
	data: IRegulationInventoryList[] | IGroupsTableList[];
	count: number;
}

const defaultRecordsData: IRegInvLandingPageData = {
	totalRecords: {
		id: tableUniqueValues[0],
		errorMessage: '',
		data: [],
		count: 0,
	},
	applicabilityRecords: {
		id: tableUniqueValues[1],
		errorMessage: '',
		data: [],
		count: 0,
	},
	groupingRegulationRecords: {
		id: tableUniqueValues[2],
		errorMessage: '',
		data: [],
		count: 0,
	},
	groupingRecords: {
		id: tableUniqueValues[3],
		errorMessage: '',
		data: [],
		count: 0,
	},
	summaryRecords: {
		id: tableUniqueValues[4],
		errorMessage: '',
		data: [],
		count: 0,
	},
	mappingRecords: {
		id: tableUniqueValues[5],
		errorMessage: '',
		data: [],
		count: 0,
	},
	completedRecords: {
		id: tableUniqueValues[6],
		errorMessage: '',
		data: [],
		count: 0,
	},
};

const RegulationInventoryLandingPage = (props: IRegInvLandingPage) => {
	const { wrapper = storageWrapper } = props;
	const { IS_EXPORT_BUTTON_ENABLED } = FeatureFlagsKeysEnum;
	const dispatch = useDispatch();

	const selectedCardDetails = wrapper.getStorageItem('selectedCardDetails');
	const selectedTabIndex = selectedCardDetails?.selectedTab || 0;
	const selectedToggleButton = useSelector(
		(state: RootState) => state.regulationInventory.regInvSelectedToggleButton,
	);

	useEffect(() => {
		scrollToTop();
	}, []);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const [isExportDataLoading, setIsExportDataLoading] =
		useState<boolean>(false);
	const [exportColumnNames, setExportColumnNames] = useState<ISelectOption[]>(
		[],
	);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [tabIndex, setTabIndex] = useState<number>(selectedTabIndex);

	const [tableData, setTableData] =
		useState<IRegInvLandingPageData>(defaultRecordsData);

	const [value, setValue] = useState('regulations');

	const [openExportModal, setOpenExportModal] = useState<boolean>(false);

	const regInvFilterData = useSelector(
		(state: RootState) => state.regulationInventory.regInventoryAppliedFilters,
	);
	const regInvSearchText = useSelector(
		(state: RootState) => state.regulationInventory.regInventorySearchText,
	);

	const regInvLandingSelectedTab = useSelector(
		(state: RootState) => state.regulationInventory.regInvLandingSelectedTab,
	);

	const breadCrumbText = useSelector(
		(state: RootState) => state.common.requirementInventoryBreadcrumbText,
	);

	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);

	const isExportButtonEnabled = useFeatureFlag(
		IS_EXPORT_BUTTON_ENABLED,
		isLDResponseExists,
	);

	const regInvSelectedCard =
		selectedCardDetails?.selectedCardName || 'Overview';

	const searchIndex = getRegInvRegulationSearchIndexData(REGULATION);
	const groupSearchIndex = getRegInvRegulationSearchIndexData(GROUP);
	let updatedSearchString = handleSpecialCharacters(regInvSearchText);

	const {
		tableQueryObj: allRegTableQuery,
		queryFlag,
		updateQueryFlag,
		updateTableQuery: setAllRegTableQuery,
	} = useTableQuery({ id: tableData.totalRecords.id });
	const {
		tableQueryObj: appRegTableQuery,
		updateTableQuery: setAppRegTableQuery,
	} = useTableQuery({ id: tableData.applicabilityRecords.id });
	const {
		tableQueryObj: groupingRegTableQuery,
		updateTableQuery: setGroupingRegTableQuery,
	} = useTableQuery({ id: tableData.groupingRegulationRecords.id });
	const {
		tableQueryObj: groupingObRegTableQuery,
		updateTableQuery: setGroupingObRegTableQuery,
	} = useTableQuery({ id: tableData.groupingRecords.id });
	const {
		tableQueryObj: summaryRegTableQuery,
		updateTableQuery: setSummaryRegTableQuery,
	} = useTableQuery({ id: tableData.summaryRecords.id });
	const {
		tableQueryObj: mappingTableQuery,
		updateTableQuery: setMappingRegTableQuery,
	} = useTableQuery({ id: tableData.mappingRecords.id });
	const {
		tableQueryObj: completedTableQuery,
		updateTableQuery: setCompleteRegTableQuery,
	} = useTableQuery({ id: tableData.completedRecords.id });

	const { updateTableQuery: regInvRelationshipUpdateTable } = useTableQuery({
		id: 'regInvRelationshipCard',
	});

	const searchError = REG_INV_NO_RECORDS_ERROR_MSG.replace(
		'[search_text]',
		regInvSearchText,
	);
	const addNewGroup = useCallback(() => {
		dispatch(setRegInvententoryCreateDrawer(true));
	}, [setRegInvententoryCreateDrawer]);

	const handleChange = (event: any, newValue: any) => {
		if (newValue && value !== newValue) {
			setValue(newValue);
			dispatch(setRegulationToggleButton(newValue));
		}
	};

	const getTableHelper = (status: string) => {
		switch (status) {
			case tableData.applicabilityRecords.id:
				return {
					storeTableQuery: appRegTableQuery,
					updateStoreQueryFn: setAppRegTableQuery,
					getAPIMethod: getApplicabilityData,
				};
			case tableData.groupingRegulationRecords.id:
				return {
					storeTableQuery: groupingRegTableQuery,
					updateStoreQueryFn: setGroupingRegTableQuery,
					getAPIMethod: getGroupRegulationTableData,
				};
			case tableData.groupingRecords.id:
				return {
					storeTableQuery: groupingObRegTableQuery,
					updateStoreQueryFn: setGroupingObRegTableQuery,
					getAPIMethod: getGroupsTableData,
				};
			case tableData.summaryRecords.id:
				return {
					storeTableQuery: summaryRegTableQuery,
					updateStoreQueryFn: setSummaryRegTableQuery,
					getAPIMethod: getSummaryTableData,
				};
			case tableData.mappingRecords.id:
				return {
					storeTableQuery: mappingTableQuery,
					updateStoreQueryFn: setMappingRegTableQuery,
					getAPIMethod: getMappingTableData,
				};
			case tableData.completedRecords.id:
				return {
					storeTableQuery: completedTableQuery,
					updateStoreQueryFn: setCompleteRegTableQuery,
					getAPIMethod: getCompleteTabTableData,
				};
			/// Default is all
			default:
				return {
					storeTableQuery: allRegTableQuery,
					updateStoreQueryFn: setAllRegTableQuery,
					getAPIMethod: getInventoryData,
				};
		}
	};

	useEffect(() => {
		updateTableData();
	}, [regInvSearchText]);

	const defaultQuery = {
		pageNumber: PAGINATION_CONSTANTS.PAGE_NUMBER,
		pageSize: PAGINATION_CONSTANTS.PAGE_SIZE,
	};

	const updateTableData = () => {
		regInvRelationshipUpdateTable({
			pageNumber: 0,
			pageSize: TABLE_VIEW_PAGE_SIZE.TABLE_SIZE_12,
		});
		updatedSearchString = handleSpecialCharacters(regInvSearchText);
		tableUniqueValues.forEach((tabId) => {
			const { storeTableQuery, updateStoreQueryFn, getAPIMethod } =
				getTableHelper(tabId);
			if (queryFlag) {
				getAPIMethod(storeTableQuery);
				updateQueryFlag(false);
			} else if (regInvSearchText) {
				getAPIMethod(storeTableQuery);
				updateStoreQueryFn?.(defaultQuery);
			} else {
				getAPIMethod(storeTableQuery);
			}
		});
	};

	const getInventoryData = async (data: any) => {
		setIsLoading(true);

		const updatedRegulationKeyFilter = getKeysForRegInvSearch(
			regInvFilterData,
			REGULATION,
		);

		const filter: IRegInvSearchParams = {
			searchText: updatedSearchString,
			index: searchIndex,
			filter: updatedRegulationKeyFilter,
		};
		const response = await getRegulationInventoryData(filter, data);
		if (!response.isError) {
			const responseData = response?.responseBody;
			const updatedRegData =
				responseData?.content?.map((item: IRegulationInventoryList) => ({
					...item,
					taskStatus: item?.taskStatus ? item.taskStatus : NOTSTARTED,
					ownerId: item.assignedGroupId || item.assignedUserId,
					owner: item.assignedGroupName || item.assignedUserName,
				})) || [];
			setTableData((prev) => ({
				...prev,
				totalRecords: {
					...prev.totalRecords,
					errorMessage:
						regInvSearchText.length > 0 && responseData?.totalCount === 0
							? searchError
							: '',
					data: updatedRegData,
					count: responseData?.totalCount ?? 0,
				},
			}));
		} else {
			setTableData((prev) => ({
				...prev,
				errorMessage: APP_GENERAL_ERROR_MSG,
				totalRecords: { ...prev.totalRecords, data: [], count: 0 },
			}));
		}
		setIsLoading(false);
	};

	const createTaskStatusFilters = (filters: any, elements: string[]) => {
		const addedFilters: string[] = [];
		if (filters.length === 0) {
			return elements;
		}
		elements.forEach((res) => {
			if (filters[0]?.values?.includes(res)) {
				addedFilters.push(res);
			}
		});
		return addedFilters;
	};

	const updateTaskStatusFilter = (elements: string[]) => {
		let currentFilter = regInvFilterData.filter(
			(res: any) => res.key !== WORK_FLOW_STATUS_TYPE,
		);
		currentFilter = [
			...currentFilter,
			{
				key: WORK_FLOW_STATUS_TYPE,
				values: createTaskStatusFilters(filters, elements),
			},
		];
		return currentFilter;
	};

	let filters = regInvFilterData.filter(
		(res: any) => res.key === WORK_FLOW_STATUS_TYPE,
	);

	const getApplicabilityData = async (data: any) => {
		setIsLoading(true);
		if (
			filters[0]?.values.includes(APPLICABILITY_ASSESSMENT) ||
			filters[0]?.values.includes(APPLICABILITY_REVIEW) ||
			regInvFilterData.length === 0
		) {
			const currentFilter = updateTaskStatusFilter([
				APPLICABILITY_ASSESSMENT,
				APPLICABILITY_REVIEW,
			]);

			const updatedRegulationKeyFilter = getKeysForRegInvSearch(
				currentFilter,
				REGULATION,
			);
			const applicablityfilter = {
				searchText: updatedSearchString,
				index: searchIndex,
				filter: updatedRegulationKeyFilter,
			};
			const response = await getRegulationInventoryData(
				applicablityfilter,
				data,
			);
			if (!response.isError) {
				const responseData = response?.responseBody;
				const updatedData =
					responseData?.content?.map((item: IRegulationInventoryList) => ({
						...item,
						ownerId: item.assignedGroupId || item.assignedUserId,
						owner: item.assignedGroupName || item.assignedUserName,
					})) || [];
				setTableData((prev) => ({
					...prev,
					applicabilityRecords: {
						...prev.applicabilityRecords,
						errorMessage:
							regInvSearchText.length > 0 && responseData?.totalCount === 0
								? searchError
								: '',
						data: updatedData,
						count: responseData?.totalCount ?? 0,
					},
				}));
				setIsLoading(false);
			} else {
				setTableData((prev) => ({
					...prev,
					applicabilityRecords: {
						...prev.applicabilityRecords,
						errorMessage: APP_GENERAL_ERROR_MSG,
						data: [],
						count: 0,
					},
				}));
				setIsLoading(false);
			}
		} else {
			setTableData((prev) => ({
				...prev,
				applicabilityRecords: {
					...prev.applicabilityRecords,
					data: [],
					count: 0,
				},
			}));
		}
	};
	const getGroupRegulationTableData = async (data: any) => {
		setIsLoading(true);
		if (
			filters[0]?.values.includes(GROUPING_ASSESSMENT) ||
			filters[0]?.values.includes(GROUPING_REVIEW) ||
			regInvFilterData.length === 0
		) {
			const currentFilter = updateTaskStatusFilter([
				GROUPING_ASSESSMENT,
				GROUPING_REVIEW,
			]);

			const updatedRegulationKeyFilter = getKeysForRegInvSearch(
				currentFilter,
				REGULATION,
			);
			const groupingFilter = {
				searchText: updatedSearchString,
				index: searchIndex,
				filter: updatedRegulationKeyFilter,
			};
			const response = await getRegulationInventoryData(groupingFilter, data);
			if (!response.isError) {
				const responseGroupData = response?.responseBody;
				const updatedGroupData =
					responseGroupData?.content?.map((item: IRegulationInventoryList) => ({
						...item,
						ownerId: item.assignedGroupId || item.assignedUserId,
						owner: item.assignedGroupName || item.assignedUserName,
					})) || [];
				setTableData((prev) => ({
					...prev,
					groupingRegulationRecords: {
						...prev.groupingRegulationRecords,
						errorMessage:
							regInvSearchText.length > 0 && responseGroupData?.totalCount === 0
								? searchError
								: '',
						data: updatedGroupData,
						count: responseGroupData?.totalCount ?? 0,
					},
				}));
				setIsLoading(false);
			} else {
				setTableData((prev) => ({
					...prev,
					groupingRegulationRecords: {
						...prev.groupingRegulationRecords,
						errorMessage: APP_GENERAL_ERROR_MSG,
						data: [],
						count: 0,
					},
				}));
				setIsLoading(false);
			}
		} else {
			setTableData((prev) => ({
				...prev,
				groupingRegulationRecords: {
					...prev.groupingRegulationRecords,
					data: [],
					count: 0,
				},
			}));
		}
	};

	const getGroupsTableData = async (data: any) => {
		if (
			filters[0]?.values.includes(GROUPING_ASSESSMENT) ||
			filters[0]?.values.includes(GROUPING_REVIEW) ||
			filters[0]?.values.includes(NOTSTARTED) ||
			regInvFilterData.length === 0
		) {
			const currentFilter = updateTaskStatusFilter([
				GROUPING_ASSESSMENT,
				GROUPING_REVIEW,
				NOTSTARTED,
			]);
			const updatedGroupKeyFilter = getKeysForRegInvSearch(
				currentFilter,
				GROUP,
			);
			const filter: IRegInvSearchParams = {
				searchText: updatedSearchString,
				index: groupSearchIndex,
				filter: updatedGroupKeyFilter,
			};
			const response = await getRegulationInventoryData(filter, data);
			if (!response.isError) {
				const responseData: any = response?.responseBody;
				setTableData((prev) => ({
					...prev,
					groupingRecords: {
						...prev.groupingRecords,
						errorMessage:
							regInvSearchText.length > 0 && responseData?.totalCount === 0
								? searchError
								: '',
						data: responseData?.content,
						count: responseData?.totalCount || 0,
					},
				}));
				setIsLoading(false);
			} else {
				setTableData((prev) => ({
					...prev,
					groupingRecords: {
						...prev.groupingRecords,
						errorMessage: APP_GENERAL_ERROR_MSG,
						data: [],
						count: 0,
					},
				}));
				setIsLoading(false);
			}
		} else {
			setTableData((prev) => ({
				...prev,
				groupingRecords: {
					...prev.groupingRecords,
					data: [],
					count: 0,
				},
			}));
		}
	};

	const getSummaryTableData = async (data: any) => {
		if (
			filters[0]?.values.includes(SUMMARY_ASSESSMENT) ||
			filters[0]?.values.includes(SUMMARY_REVIEW) ||
			regInvFilterData.length === 0
		) {
			const currentFilter = updateTaskStatusFilter([
				SUMMARY_ASSESSMENT,
				SUMMARY_REVIEW,
			]);
			const updatedGroupKeyFilter = getKeysForRegInvSearch(
				currentFilter,
				GROUP,
			);
			const filter: IRegInvSearchParams = {
				searchText: updatedSearchString,
				index: groupSearchIndex,
				filter: updatedGroupKeyFilter,
			};
			const response = await getRegulationInventoryData(filter, data);
			if (!response.isError) {
				const responseData: any = response?.responseBody;
				setTableData((prev) => ({
					...prev,
					summaryRecords: {
						...prev.summaryRecords,
						errorMessage:
							regInvSearchText.length > 0 && responseData?.totalCount === 0
								? searchError
								: '',
						data: responseData?.content,
						count: responseData?.totalCount || 0,
					},
				}));
				setIsLoading(false);
			} else {
				setTableData((prev) => ({
					...prev,
					summaryRecords: {
						...prev.summaryRecords,
						errorMessage: APP_GENERAL_ERROR_MSG,
						data: [],
						count: 0,
					},
				}));
				setIsLoading(false);
			}
		} else {
			setTableData((prev) => ({
				...prev,
				summaryRecords: {
					...prev.summaryRecords,
					data: [],
					count: 0,
				},
			}));
		}
	};

	const handleMappingSuccessResponse = (response: any) => {
		const responseData: any = response?.responseBody;
		const mappingData = responseData?.content || [];
		let APIResponse: IGroupsTableList[] = [];
		try {
			APIResponse = mappingData.map((element: any) => ({
				...element,
				businessUnitDetailDTOS:
					element?.businessUnitDetailDTOS?.map((ele: any) => JSON.parse(ele)) ||
					[],
			}));
		} catch (e) {
			APIResponse = [];
		}
		setTableData((prev) => ({
			...prev,
			mappingRecords: {
				...prev.mappingRecords,
				errorMessage:
					regInvSearchText.length > 0 && responseData?.totalCount === 0
						? searchError
						: '',
				data: APIResponse,
				count: responseData?.totalCount || 0,
			},
		}));
		setIsLoading(false);
	};

	const getMappingTableData = async (data: any) => {
		if (
			filters[0]?.values.includes(MAPPING_ASSESSMENT) ||
			filters[0]?.values.includes(MAPPING_REVIEW) ||
			regInvFilterData.length === 0
		) {
			const currentFilter = updateTaskStatusFilter([
				MAPPING_ASSESSMENT,
				MAPPING_REVIEW,
			]);
			const updatedGroupKeyFilter = getKeysForRegInvSearch(
				currentFilter,
				GROUP,
			);
			const filter = {
				searchText: updatedSearchString,
				index: groupSearchIndex,
				filter: updatedGroupKeyFilter,
			};
			const response = await getRegulationInventoryData(filter, data);
			if (!response.isError) {
				handleMappingSuccessResponse(response);
			} else {
				setTableData((prev) => ({
					...prev,
					mappingRecords: {
						...prev.mappingRecords,
						errorMessage: APP_GENERAL_ERROR_MSG,
						data: [],
						count: 0,
					},
				}));
			}
		} else {
			setTableData((prev) => ({
				...prev,
				mappingRecords: {
					...prev.mappingRecords,
					data: [],
					count: 0,
				},
			}));
		}
	};

	const getCompleteTabTableData = async (data: any) => {
		if (
			filters[0]?.values.includes(COMPLETE) ||
			regInvFilterData.length === 0
		) {
			const currentFilter = updateTaskStatusFilter([COMPLETE]);

			const completeFilters = getKeysForRegInvSearch(currentFilter, GROUP);

			const filter: IRegInvSearchParams = {
				searchText: updatedSearchString,
				index: groupSearchIndex,
				filter: completeFilters,
			};

			const response = await getRegulationInventoryData(filter, data);
			if (!response.isError) {
				const responseData: any = response?.responseBody;
				setTableData((prev) => ({
					...prev,
					completedRecords: {
						...prev.completedRecords,
						errorMessage:
							regInvSearchText.length > 0 && responseData?.totalCount === 0
								? searchError
								: '',
						data: responseData?.content || [],
						count: responseData?.totalCount || 0,
					},
				}));
				setIsLoading(false);
			} else {
				setTableData((prev) => ({
					...prev,
					completedRecords: {
						...prev.completedRecords,
						errorMessage: APP_GENERAL_ERROR_MSG,
						data: [],
						count: 0,
					},
				}));
				setIsLoading(false);
			}
		} else {
			setTableData((prev) => ({
				...prev,
				completedRecords: {
					...prev.completedRecords,
					data: [],
					count: 0,
				},
			}));
		}
	};

	const changeSearchInput = (event: any) => {
		const text = event.target.value;
		dispatch(setRegInventorySearchText(text));
	};
	const debouncedSearchHandler = debounce(changeSearchInput, 500);

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	useEffect(() => {
		setTabIndex(selectedTabIndex);
		wrapper.setStorageItem('selectedCardDetails', {
			...selectedCardDetails,
			selectedTab: selectedTabIndex,
		});
		setValue(selectedToggleButton);
	}, [tabIndex]);

	const onTabSelect = (index: number) => {
		setTabIndex(index);
		wrapper.setStorageItem('selectedCardDetails', {
			...selectedCardDetails,
			selectedTab: index,
		});
	};

	const tabs = [
		{
			id: 1,
			key: tableData.totalRecords.id,
			children: (
				<AllRegulationsTable
					id={tableData.totalRecords.id}
					setIsLoading={setIsLoading}
					isLoading={isLoading}
					inventoryTableData={
						tableData.totalRecords.data as IRegulationInventoryList[]
					}
					errorMessage={tableData.totalRecords.errorMessage}
					getInventoryData={getInventoryData}
					getApplicabilityData={getApplicabilityData}
					getGroupingData={getGroupRegulationTableData}
					totalRecordsCount={tableData.totalRecords.count}
				/>
			),
		},
		{
			id: 2,
			key: tableData.applicabilityRecords.id,
			children: (
				<AllRegulationsTable
					id={tableData.applicabilityRecords.id}
					isLoading={isLoading}
					errorMessage={tableData.applicabilityRecords.errorMessage}
					inventoryTableData={
						tableData.applicabilityRecords.data as IRegulationInventoryList[]
					}
					getInventoryData={getApplicabilityData}
					getGroupingData={getGroupRegulationTableData}
					getApplicabilityData={getInventoryData}
					totalRecordsCount={tableData.applicabilityRecords.count}
				/>
			),
		},
		{
			id: 3,
			key: tableData.groupingRegulationRecords.id,
			children:
				value === 'regulations' ? (
					<AllRegulationsTable
						id={tableData.groupingRegulationRecords.id}
						isLoading={isLoading}
						errorMessage={tableData.groupingRegulationRecords.errorMessage}
						inventoryTableData={
							tableData.groupingRegulationRecords
								.data as IRegulationInventoryList[]
						}
						getApplicabilityData={getApplicabilityData}
						getGroupingData={getInventoryData}
						getInventoryData={getGroupRegulationTableData}
						totalRecordsCount={tableData.groupingRegulationRecords.count}
					/>
				) : (
					<GroupsTable
						id={tableData.groupingRecords.id}
						isLoading={isLoading}
						inventoryTableData={
							tableData.groupingRecords.data as IGroupsTableList[]
						}
						errorMessage={tableData.groupingRecords.errorMessage}
						getTableData={getGroupsTableData}
						totalRecordsCount={tableData.groupingRecords.count}
						selectedTabValue="Grouping"
					/>
				),
		},
		{
			id: 4,
			key: tableData.summaryRecords.id,
			children: (
				<GroupsTable
					id={tableData.summaryRecords.id}
					isLoading={isLoading}
					inventoryTableData={
						tableData.summaryRecords.data as IGroupsTableList[]
					}
					getTableData={getSummaryTableData}
					errorMessage={tableData.summaryRecords.errorMessage}
					totalRecordsCount={tableData.summaryRecords.count}
					selectedTabValue="Summary"
				/>
			),
		},
		{
			id: 5,
			key: tableData.mappingRecords.id,
			children: (
				<MappingTable
					id={tableData.mappingRecords.id}
					isLoading={isLoading}
					inventoryTableData={
						tableData.mappingRecords.data as IGroupsTableList[]
					}
					getTableData={getMappingTableData}
					errorMessage={tableData.mappingRecords.errorMessage}
					totalRecordsCount={tableData.mappingRecords.count}
				/>
			),
		},
		{
			id: 6,
			key: tableData.completedRecords.id,
			children: (
				<CompleteTabTable
					id={tableData.completedRecords.id}
					isLoading={isLoading}
					inventoryTableData={
						tableData.completedRecords.data as IGroupsTableList[]
					}
					getTableData={getCompleteTabTableData}
					errorMessage={tableData.completedRecords.errorMessage}
					totalRecordsCount={tableData.completedRecords.count}
				/>
			),
		},
	];

	const tabDetails: ITabValues[] = [
		{
			name: 'All Requirements',
			count: formatNumber(tableData.totalRecords.count),
		},
		{
			name: 'Applicability ',
			count: formatNumber(tableData.applicabilityRecords.count),
		},
		{
			name: 'Grouping ',
			count:
				value === 'regulations'
					? formatNumber(tableData.groupingRegulationRecords.count)
					: formatNumber(tableData.groupingRecords.count),
		},
		{
			name: 'Summary ',
			count: formatNumber(tableData.summaryRecords.count),
		},
		{
			name: 'Mapping ',
			count: formatNumber(tableData.mappingRecords.count),
		},
		{
			name: 'Complete ',
			count: formatNumber(tableData.completedRecords.count),
		},
	];

	const applyFilters = () => {
		tableUniqueValues.forEach((tabId) => {
			const { updateStoreQueryFn } = getTableHelper(tabId);
			updateStoreQueryFn?.(defaultQuery);
		});
		updateTableData();
	};

	const exportHandler = async () => {
		setIsExportDataLoading(true);
		const resp = await getExportColNames('REGULATION_INVENTORY_EXPORT_COLUMNS');
		if (resp?.statusCode === OK) {
			setIsExportDataLoading(false);
			const response = resp?.responseBody as IExportDataResponse;
			setExportColumnNames(getMappedColumnNamesFromResponse(response));
			setOpenExportModal(true);
		} else {
			setIsExportDataLoading(false);
			dispatch(
				setToastMessage({
					message: NO_COLUMN_DATA,
					severity: 'error',
					isToastOpen: true,
				}),
			);
			onCloseHandler();
		}
	};

	const onCloseHandler = () => {
		setOpenExportModal(false);
	};

	const breadCrumbs: ICustomBreadcrumbsProps[] = [
		{
			title: toSentenceCase(breadCrumbText),
			type: 'link',
			key: 'req-inv',
			path: rrcpDashboardRoutes.regulationInventory,
			dataTestId: 'inventory-breadcrumbs-link',
		},
		{
			title: toSentenceCase(regInvSelectedCard),
			type: 'text',
			key: 'static-text',
		},
	];

	const isExportDisabled =
		tableData.completedRecords.count === 0 ||
		isExportButtonEnabled ||
		tabIndex !== 5;

	return (
		<Grid xs={12} data-testid="reg-inv-testId">
			<Grid item pt={0}>
				<RegulationBreadcrumbs items={breadCrumbs} storageWrapper={wrapper} />
				<Banner>
					<Stack p={2} className={styles['regulation-header-wrapper']}>
						<Typography
							className={
								styles['regulation-inventory-landingpage-banner-heading']
							}
						>
							{toSentenceCase(MODULES.REQUIREMENT_INVENTORY)}
						</Typography>
					</Stack>
				</Banner>
				<PageTitle
					text={RegulatoryInventoryLanding}
					childComponent={
						<div>
							{value === 'groups' && tabIndex === 2 && (
								<ButtonComp
									btnType="secondary"
									data-testid="create-btn"
									clickHandler={addNewGroup}
									startIcon={
										<Box className={styles['filter-icon']}>
											<Plus />
										</Box>
									}
								>
									{toSentenceCase('Create Group')}
								</ButtonComp>
							)}
						</div>
					}
				/>
			</Grid>
			<Grid className={`${styles['main-content']}`}>
				<Stack className={styles['filter-bar']}>
					<StyledTextField
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchOutlined className={styles['input-search-icon']} />
								</InputAdornment>
							),
							inputProps: { 'data-testid': 'reginv-elastic-search' },
						}}
						defaultValue={regInvSearchText}
						onChange={debouncedSearchHandler}
						id="fullWidth"
						placeholder="Search"
						data-testid="searchInput"
						size="small"
					/>
					<div className={styles['filter-button']}>
						<AdvancedFilter applyFilterClicked={applyFilters} />
					</div>
				</Stack>
				<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
					<TabBar
						activeTab={tabIndex}
						details={tabDetails}
						components={tabs}
						testId="tab-list"
						showCount
						showButton
						onTabChange={onTabSelect}
						childComponent={
							<Grid item display="flex" gap="1rem">
								{tabIndex === 2 && (
									<ToggleButtonGroup
										className={styles['togglebutton-group-style']}
										sx={{
											fontWeight: (theme) => theme.typography.fontWeightRegular,
											marginRight: (theme) => theme.spacing(1),
										}}
										value={value}
										exclusive
										onChange={handleChange}
										aria-label="text alignment"
									>
										<ToggleButton
											value="regulations"
											aria-label="left aligned"
											sx={toggleButtonStyle.select}
										>
											{REQUIREMENTS}
										</ToggleButton>
										<ToggleButton
											value="groups"
											data-testId="groups-click"
											aria-label="right aligned"
											sx={toggleButtonStyle.select}
										>
											{OBLIGATIONS}
										</ToggleButton>
									</ToggleButtonGroup>
								)}
								{regInvLandingSelectedTab === CURRENT_INVENTORY && (
									<ButtonComp
										compClass={
											tabIndex === 2
												? styles['export-button-expanded']
												: styles['export-button']
										}
										disabled={isExportDisabled}
										clickHandler={exportHandler}
										data-testid="export-btn"
										btnType="secondary"
									>
										<div className={styles['export-icon']}>
											<VectorExport
												fill={
													isExportDisabled
														? exportDisabled
														: activeIconColorPrimary
												}
											/>
										</div>
										<Typography className={styles['export-text']}>
											{EXPORT}
										</Typography>
									</ButtonComp>
								)}
							</Grid>
						}
					/>
				</Grid>
			</Grid>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
			{(openExportModal || isExportDataLoading) && (
				<ExportData
					onCloseModal={onCloseHandler}
					refFilterData={regInvFilterData}
					refSearchText={regInvSearchText}
					colNamesMapping={exportColumnNames}
					exportTypeName="reg-inventory"
					optionsData={exportColumnNames}
					exportedCount={tableData.completedRecords.count}
					moduleType={REQUIREMENT_INVENTORY}
					isDataLoading={isExportDataLoading}
				/>
			)}
		</Grid>
	);
};

export default RegulationInventoryLandingPage;
